import {
  BaseFormGeneratorViewModel,
  FormGeneratorViewProps,
} from "@graffer-inc/form-generator";
import {
  FormError,
  FormSchema,
  FormData as GrafferFormData,
  FormDataChangeDetector,
  MyNumberRawValue,
  FormTouched,
} from "@graffer-inc/form-schema";
import {
  ApplicationContentType,
  AttachFileWithURL,
  deepCopy,
  ModificationType,
} from "@graffer-inc/cloud-gov-core";
import { ProcedureDetailType } from "../procedures/procedure-entity";
import { ApplicationEntity } from "../application/application-entity";
import { ModifyFileExchanger } from "./modify-file-exchanger";
import {
  ModifyingMyNumber,
  ModifyMyNumberExchanger,
} from "./modify-mynumber-exchanger";

export type ModifyFormGeneratorProps = FormGeneratorViewProps & {
  isValidForm: boolean;
  isModified: boolean;
  fileExchanger: ModifyFileExchanger;
  myNumberExchanger: ModifyMyNumberExchanger;
  detector: FormDataChangeDetector;
  formSubmissionContext: ModifyFormViewModel["formSubmissionContext"];
};

export class ModifyFormViewModel extends BaseFormGeneratorViewModel<ModifyFormGeneratorProps> {
  constructor(
    public readonly formSchema: FormSchema,
    public values: GrafferFormData,
    public errors: FormError,
    public sectionErrors: FormError[],
    public touched: FormTouched,
    public readonly original: GrafferFormData,
    public readonly originalFiles: { [key: string]: AttachFileWithURL },
    public readonly originalMyNumbers: { [key: string]: MyNumberRawValue },
    public readonly fileExchanger: ModifyFileExchanger,
    public readonly myNumberExchanger: ModifyMyNumberExchanger,
    formSubmissionContext: ModifyFormGeneratorProps["formSubmissionContext"],
  ) {
    super(
      formSchema,
      values,
      errors,
      sectionErrors,
      touched,
      () => ({
        ...this.fileExchanger.getRegistry(),
        ...this.myNumberExchanger.getRegistry(),
      }),
      formSubmissionContext,
    );
  }

  getProps(): ModifyFormGeneratorProps {
    const detector = new FormDataChangeDetector({
      current: this.values,
      original: this.original,
      originalFiles: this.originalFiles,
      originalMyNumbers: this.originalMyNumbers,
    });
    return {
      values: this.values,
      formSchema: this.formSchema,
      errors: this.errors,
      sectionErrors: this.sectionErrors,
      touched: this.touched,
      handleChange: this.handleChange.bind(this),
      handleBlur: this.handleBlur.bind(this),
      handleFocus: this.handleFocus.bind(this),
      setFieldValue: this.setFieldValue.bind(this),
      setFieldTouched: this.setFieldTouched.bind(this),
      setTouched: this.setTouched.bind(this),
      isValidForm: !this.errors || !Object.keys(this.errors).length,
      isModified: detector.checkAll(),
      fileExchanger: this.fileExchanger,
      myNumberExchanger: this.myNumberExchanger,
      detector: detector,
      formSubmissionContext: this.formSubmissionContext,
    };
  }

  static create(args: {
    procedure: ProcedureDetailType;
    application: ApplicationEntity;
    modificationType: ModificationType;
  }): ModifyFormViewModel {
    const content: ApplicationContentType =
      args.modificationType === "ORG_USER_DECISION"
        ? {
            jsonDocument: args.application.rawDocument,
            files: args.application.files,
            myNumbers: args.application.myNumbers,
          }
        : args.application.getApplicantContent();
    const formSchema = FormSchema.fromJSON(args.procedure.template.formSchema);
    const initialValues = deepCopy(content.jsonDocument);

    const files: {
      [key: string]: AttachFileWithURL;
    } = {};
    content.files?.forEach((f) => {
      if (f.refKey) {
        files[f.refKey] = f;
      }
    });
    const fileExchanger = new ModifyFileExchanger(files, content);

    const mynumbers: {
      [key: string]: ModifyingMyNumber;
    } = {};
    const originalMyNumbers: {
      [key: string]: MyNumberRawValue;
    } = {};
    content.myNumbers?.forEach((m) => {
      if (m.refKey) {
        mynumbers[m.refKey] = {
          refKey: m.refKey,
          value: "000000000000",
          isModified: false,
          myNumberId: m.myNumberId,
          last4Digit: m.last4Digit,
        };
        originalMyNumbers[m.refKey] = {
          value: `********${m.last4Digit}`,
        };
      }
    });
    const myNumberExchanger = new ModifyMyNumberExchanger(
      args.application.id,
      mynumbers,
    );

    return new ModifyFormViewModel(
      formSchema,
      initialValues,
      {},
      [],
      formSchema.makeTouched(initialValues),
      content.jsonDocument,
      { ...files },
      { ...originalMyNumbers },
      fileExchanger,
      myNumberExchanger,
      { submissionDate: args.application.createdAtDate },
    );
  }
}
